import type { ReactNode } from "react";
import { House, Swords, Trophy, Wallet } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { SeasonArchive } from "@averagejoesff/shared";
import { BlowoutView } from "./blowout/BlowoutView";
import { DuesView } from "./dues/DuesView";
import { HomeView } from "./home/HomeView";
import { ManagersView } from "./managers/ManagersView";
import { HALL_OF_FAME_PATH, LUCK_PATH, MANAGERS_PATH, MILESTONES_PATH } from "./managers/paths";
import { MatchupsView } from "./matchups/MatchupsView";
import { PrizePayoutsView } from "./payouts/PrizePayoutsView";

export type FeatureId = "home" | "biggestBlowout" | "dues" | "prizePayouts" | "matchups" | "managers";

/**
 * Which seasons a season-scoped feature lets the user browse (see App.tsx's
 * `seasonScope` handling): every published season, only the current one (no
 * picker at all), or everything from a fixed floor season onward.
 */
export type SeasonScope = "all" | "current" | { minSeason: number };

/** Prize Payouts only tracks winners from this season onward — dollar-for-dollar
 *  history further back isn't useful to the league. */
const PAYOUT_HISTORY_MIN_SEASON = 2023;

/** What routing and page titles need for every feature, regardless of whether
 *  it's season-scoped or site-wide. (How the nav groups them is `NAV_GROUPS`.) */
export interface NavFeature {
  id: FeatureId;
  label: string;
  /** The shareable URL path for this page (see `lib/router.ts`). */
  path: string;
  /** Also owns every path beneath `path` (e.g. `/managers/<id>`), which the page reads itself. */
  matchSubpaths?: boolean;
}

interface SeasonFeature extends NavFeature {
  seasonScope: SeasonScope;
  /**
   * `toolbar` is the season picker — threaded through so each view can slot
   * it into its own `.feature__intro` row instead of App.tsx rendering it
   * as a separate block above every season-scoped feature.
   */
  render: (archive: SeasonArchive, toolbar: ReactNode) => ReactNode;
}

interface SiteWideFeature extends NavFeature {
  render: () => ReactNode;
}

/**
 * Season-scoped features: read from the currently-loaded `SeasonArchive`
 * (see App.tsx). Adding one is a single entry here — mirrors
 * `@averagejoesff/shared`'s `awards/index.ts` `builders` registry, the same
 * pattern for the same reason.
 */
export const SEASON_FEATURES: SeasonFeature[] = [
  {
    id: "biggestBlowout",
    label: "Biggest Blowout",
    path: "/blowout",
    seasonScope: "all",
    render: (archive, toolbar) => <BlowoutView archive={archive} toolbar={toolbar} />,
  },
  {
    id: "dues",
    label: "Dues Tracker",
    path: "/dues",
    // No season picker: dues are current-season-only (see App.tsx).
    seasonScope: "current",
    render: (archive) => <DuesView archive={archive} />,
  },
  {
    id: "prizePayouts",
    label: "Prize Payouts",
    path: "/payouts",
    seasonScope: { minSeason: PAYOUT_HISTORY_MIN_SEASON },
    render: (archive, toolbar) => <PrizePayoutsView archive={archive} toolbar={toolbar} />,
  },
];

/**
 * Site-wide features: self-fetching, not gated on a season having loaded.
 * Matchups lives here because each skill-authored report carries its own
 * season/week (and its own week picker) rather than reading a `SeasonArchive`.
 */
export const SITE_WIDE_FEATURES: SiteWideFeature[] = [
  {
    // Reads the league history, so it's site-wide rather than season-scoped.
    id: "home",
    label: "Home",
    path: "/",
    render: () => <HomeView />,
  },
  {
    id: "matchups",
    label: "Matchups",
    path: "/matchups",
    render: () => <MatchupsView />,
  },
  {
    id: "managers",
    label: "Managers",
    path: MANAGERS_PATH,
    matchSubpaths: true,
    render: () => <ManagersView />,
  },
];

/** Every feature, for routing and page titles. */
const NAV_FEATURES: NavFeature[] = [...SEASON_FEATURES, ...SITE_WIDE_FEATURES].map(
  ({ id, label, path, matchSubpaths }): NavFeature => ({ id, label, path, matchSubpaths }),
);

/** One destination in the nav: a feature, or a fixed view of one (Milestones, Hall of Fame and Luck are views of Managers). */
export interface NavPage {
  /** The nav's text, kept short enough for a phone-width row of tabs. */
  label: string;
  /** The page's own name for the browser tab and bookmarks, when it differs from `label`. */
  title?: string;
  path: string;
  featureId: FeatureId;
}

/**
 * What the header and drawer show: a few top-level groups instead of one tab
 * per page. A group with several pages gets a row of section tabs on each of
 * its pages (and a heading with its pages beneath it in the drawer); a group
 * with one is just a link. Every page keeps its own URL, so grouping never
 * breaks a shared link.
 */
export interface NavGroup {
  id: string;
  label: string;
  icon: LucideIcon;
  pages: NavPage[];
}

const featurePage = (id: FeatureId): NavPage => {
  const feature = NAV_FEATURES.find((f) => f.id === id);
  if (!feature) throw new Error(`No feature registered with id ${id}`);
  return { label: feature.label, path: feature.path, featureId: id };
};

export const NAV_GROUPS: NavGroup[] = [
  { id: "home", label: "Home", icon: House, pages: [featurePage("home")] },
  { id: "matchups", label: "Matchups", icon: Swords, pages: [featurePage("matchups")] },
  {
    id: "league",
    label: "League",
    icon: Trophy,
    pages: [
      featurePage("managers"),
      { label: "Milestones", path: MILESTONES_PATH, featureId: "managers" },
      { label: "Hall of Fame", path: HALL_OF_FAME_PATH, featureId: "managers" },
      { label: "Luck", path: LUCK_PATH, featureId: "managers" },
      // Full-length labels don't fit one phone-width tab row, so the tab says "Blowout" while the page keeps its name.
      { ...featurePage("biggestBlowout"), label: "Blowout", title: featurePage("biggestBlowout").label },
    ],
  },
  { id: "money", label: "Money", icon: Wallet, pages: [featurePage("dues"), featurePage("prizePayouts")] },
];

/**
 * The group and page a URL belongs to, or null for a path no page owns. An
 * exact path wins; otherwise a page that reads its own sub-paths owns them
 * (`/managers/<id>` is the Managers page).
 */
export function activeNavPage(pathname: string): { group: NavGroup; page: NavPage } | null {
  const entries = NAV_GROUPS.flatMap((group) => group.pages.map((page) => ({ group, page })));
  const exact = entries.find(({ page }) => page.path === pathname);
  if (exact) return exact;
  return (
    entries
      .filter(({ page }) => NAV_FEATURES.find((f) => f.id === page.featureId)?.matchSubpaths && pathname.startsWith(`${page.path}/`))
      .sort((a, b) => b.page.path.length - a.page.path.length)[0] ?? null
  );
}

/** The default page, shown at "/" — must match the feature whose `path` is "/" above. */
export const DEFAULT_FEATURE_ID: FeatureId = "home";

/** Maps a URL pathname to the feature it should render, or `null` if it
 *  doesn't match any known page (App.tsx redirects that case to "/"). */
export function featureIdForPath(pathname: string): FeatureId | null {
  return (
    NAV_FEATURES.find((f) => f.path === pathname || (f.matchSubpaths && pathname.startsWith(`${f.path}/`)))?.id ?? null
  );
}
